"use client";

import { useTranslation } from "@/app/i18n/client";

import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "../../ui/input";
import { cn } from "@/lib/utils";
import { Button } from "../../ui/button";
import { ForgotPasswordValidatorSchema, forgotPasswordValidatorSchema } from "@/lib/validators/forgot-password";
import { setNewPassword, setNewPasswordForgot } from "@/actions";
import { useState, useContext } from "react";
import ColorStyleContext from "@/lib/providers/color-style-privider";
import toast from "react-hot-toast";
import { Loader2 } from "lucide-react";
// import toast from "react-hot-toast";
import { borderRadiusShorthand, useLayoutStyle } from "@/lib/providers/layout-style-provider";

interface SetPasswordFormProps {
    username: string;
    onUpdatePassword?: (success: boolean) => void;
    resetPasswordToken?: string;
}

const SetPasswordForm: React.FC<SetPasswordFormProps> = ({ username, onUpdatePassword, resetPasswordToken }) => {
    const { t } = useTranslation();
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const form = useForm<ForgotPasswordValidatorSchema>({
        resolver: zodResolver(forgotPasswordValidatorSchema),
    });

    const colorStyle = useContext(ColorStyleContext);
    const layoutStyle = useLayoutStyle();

    const onSubmit = (values: ForgotPasswordValidatorSchema) => {
        if (values.password === values.confirm_password) {
            setIsLoading(true);
            if (resetPasswordToken) {
                setNewPasswordForgot(resetPasswordToken, values.password, values.confirm_password)
                    .then((res) => {
                        // console.log('setNewPassword', res)
                        if (res && res.result.success == true) {
                            toast.success(`${t("set_password_success_toast_message")}`, {
                                position: "bottom-center",
                                duration: 3000,
                            });
                            if (onUpdatePassword) onUpdatePassword(true);
                        }
                        // setIsLoading(false)
                    })
                    .catch((error: any) => {
                        toast.error(`${t("toast_failed_message")}`, {
                            position: "bottom-center",
                            duration: 3000,
                        });
                    })
                    .finally(() => {
                        setTimeout(() => {
                            setIsLoading(false);
                        }, 3000);
                    });
            } else {
                setNewPassword(values.password, values.confirm_password)
                    .then((res) => {
                        // console.log('setNewPassword', res)
                        if (res && res.result.success == true) {
                            toast.success(`${t("set_password_success_toast_message")}`, {
                                position: "bottom-center",
                                duration: 3000,
                            });
                            if (onUpdatePassword) onUpdatePassword(true);
                        }
                        // setIsLoading(false)
                    })
                    .catch((error: any) => {
                        toast.error(`${t("toast_failed_message")}`, {
                            position: "bottom-center",
                            duration: 3000,
                        });
                    })
                    .finally(() => {
                        setTimeout(() => {
                            setIsLoading(false);
                        }, 3000);
                    });
            }
        } else {
            // toast.error(`ไม่สามารถเข้าสู่ระบบได้ กรุณาลองใหม่อีกครั้ง`, { position: "bottom-center" });
        }
    };

    return (
        <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4 p-6">
                <FormLabel className="text-lg" style={{ color: colorStyle?.text || "#3d3d3d" }}>{t("forgot_password.title_set_account_password")}</FormLabel>
                <FormDescription className="font-light text-sm " style={{ color: colorStyle?.text || "#3d3d3d" }}>
                    {t("forgot_password.set_password.description")}
                </FormDescription>
                <FormField
                    control={form.control}
                    name="password"
                    render={({ field, formState }) => (
                        <FormItem>
                            <FormLabel 
                                className="form-input-label" 
                                style={{
                                    color: colorStyle?.text || "#3d3d3d",
                                }}
                            >
                                {t("forgot_password.set_password.input_password")}
                            </FormLabel>
                            <FormControl>
                                <Input
                                    type="password"
                                    {...field}
                                    value={field.value || ""}
                                    maxLength={20}
                                    className={cn("form-input", formState.errors.password && "form-input-error")}
                                />
                            </FormControl>
                            <FormDescription className="font-light text-sm text-grayc3">
                                {t("forgot_password.set_password.text_example_password")}
                            </FormDescription>
                            <FormMessage translate="yes" />
                        </FormItem>
                    )}
                />
                <FormField
                    control={form.control}
                    name="confirm_password"
                    render={({ field, formState }) => (
                        <FormItem>
                            <FormLabel 
                                className="form-input-label"
                                style={{
                                    color: colorStyle?.text || "#3d3d3d",
                                }}
                            >
                                {t("forgot_password.set_password.input_confirm_password")}
                            </FormLabel>
                            <FormControl>
                                <Input
                                    type="password"
                                    {...field}
                                    value={field.value || ""}
                                    maxLength={20}
                                    className={cn(
                                        "form-input",
                                        formState.errors.confirm_password && "form-input-error",
                                    )}
                                />
                            </FormControl>
                            <FormMessage translate="yes" />
                        </FormItem>
                    )}
                />
                <div className="w-full flex flex-col items-center">
                    <button
                        type="submit"
                        className="w-2/3 max-w-[260px] h-[48px] dynamic-bg dynamic-color flex items-center justify-center text-lg font-normal"
                        style={
                            {
                                "--dynamic-bg": colorStyle?.buttonBgNormal || "#009eca",
                                "--dynamic-hover-bg": colorStyle?.buttonBgHover || "#254b7c",
                                "--dynamic-color": colorStyle?.buttonTextNormal || "#ffffff",
                                "--dynamic-hover-color": colorStyle?.buttonTextHover || "#ffffff",
                                "--dynamic-focus-color": colorStyle?.buttonTextHover || "#ffffff",
                                borderRadius: borderRadiusShorthand(layoutStyle),
                            } as React.CSSProperties
                        }>
                        {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                        {t("forgot_password.set_password.btn_create_password")}
                    </button>
                </div>
            </form>
        </Form>
    );
};

export default SetPasswordForm;
